import { defineStore } from 'pinia'
import { ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import type { Meeting } from '@app/application/client_meetings/types'
import * as MeetingsApi from '@app/application/client_meetings/api/meetings'
import * as PastApi from '@app/application/client_meetings/api/past'
import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import { marked } from 'marked'

export const usePastMeetingsStore = defineStore('pastMeetings', () => {
  const orderedMeetingIds = ref<number[]>([])
  const meetingsById = ref<Record<number, Meeting>>({})
  const htmlSummary = ref<Record<number, string>>({})
  const proposalDetailsStore = useProposalDetailsStore()

  function deleteMeeting(meetingId: number): void {
    orderedMeetingIds.value = orderedMeetingIds.value.filter(
      (id: number) => id !== meetingId
    )
    delete meetingsById.value[meetingId]
  }

  async function fetchPastMeetings(): Promise<void> {
    try {
      const pastMeetings = (await PastApi.fetchMeetings()) || []
      await setPastMeetings(pastMeetings)
    } catch (error) {
      SfLog.error('Failed to fetch past meetings', { error })
    }
  }

  async function setPastMeetings(
    pastMeetings: PastApi.Meeting[]
  ): Promise<void> {
    await Promise.all(
      pastMeetings.map(async (meeting) => {
        if (meeting.followUpProposal) {
          await proposalDetailsStore.addProposalDetails(
            meeting.followUpProposal
          )
        }
      })
    )

    orderedMeetingIds.value = pastMeetings.map((meeting) => meeting.id)

    meetingsById.value = pastMeetings.reduce(
      (acc, meeting) => ({
        ...acc,
        [meeting.id]: {
          id: meeting.id,
          title: meeting.title,
          summary: meeting.summary,
          completedAt: meeting.completedAt,
          order: meeting.order,
          links: meeting.links,
          proposalId: meeting.followUpProposal
            ? meeting.followUpProposal.id
            : null,
          discussedTodoIds: meeting.discussedTodoIds,
          attachments: meeting.attachments,
        },
      }),
      {}
    )

    const summaryPromises = pastMeetings.map(async (meeting) => {
      let meetingHtmlSummary = ''
      try {
        meetingHtmlSummary = meeting.summary
          ? await marked.parse(meeting.summary)
          : ''
      } catch (error) {
        SfLog.error('Error parsing meeting markdown:', { error })
      }
      return { id: meeting.id, html: meetingHtmlSummary }
    })

    const summaryResults = await Promise.all(summaryPromises)
    htmlSummary.value = summaryResults.reduce(
      (acc, { id, html }) => ({
        ...acc,
        [id]: html,
      }),
      {}
    )
  }

  function setMeetingOrder(meetingId: number, newOrder: string[]): void {
    if (meetingsById.value[meetingId]) {
      meetingsById.value[meetingId].order = newOrder
    }
  }

  async function save(meeting: Meeting): Promise<void> {
    try {
      await MeetingsApi.update(meeting)

      meetingsById.value[meeting.id] = {
        ...meetingsById.value[meeting.id],
        title: meeting.title,
        completedAt: meeting.completedAt,
        summary: meeting.summary,
      }

      htmlSummary.value[meeting.id] = meeting.summary
        ? await marked.parse(meeting.summary)
        : ''
    } catch (error) {
      SfLog.error('Failed to update meeting details:', { error })
    }
  }

  return {
    orderedMeetingIds,
    meetingsById,
    htmlSummary,
    setPastMeetings,
    fetchPastMeetings,
    setMeetingOrder,
    save,
    deleteMeeting,
  }
})
